import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Input, Popconfirm, Space, Table, TableColumnType } from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import ModalAddContact from "../../components/modal/ModalAddContact";
import ModalEditContact from "../../components/modal/ModalEditContact";
import { IContact, IContactQuery } from "../../models/Sale";
import { useDeleteContact, useGetContact } from "../../query/SaleQuery";
import { notify } from "../../utils/toast";

const Contacts = () => {
  const [params, setParams] = useState<IContactQuery>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [openModalAdd, setOpenModalAdd] = useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);

  const { data, isLoading } = useGetContact(params);
  const deleteContact = useDeleteContact();

  const handleSearchContact = useCallback(
    debounce((value: string) => {
      setParams((prev) => ({
        ...prev,
        search: value,
        page: 1,
      }));
    }, 500),
    [],
  );

  const handleDeleteContact = async (id: number) => {
    try {
      await deleteContact.mutateAsync({ id: id, query: params });
      notify.success("Xóa liên hệ thành công");
    } catch (error) {
      notify.error("Xóa liên hệ thất bại");
    }
  };

  const handleEditContact = (record: IContact) => {
    setSelectedContact(record);
    setOpenModalEdit(true);
  };

  const columns: TableColumnType<IContact>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: 80,
    },
    {
      title: "Tên liên hệ",
      dataIndex: "name",
      key: "name",
      align: "left",
      filterDropdown: () => (
        <Input
          placeholder="Tìm kiếm theo tên"
          onChange={(e) => handleSearchContact(e.target.value)}
          prefix={<SearchOutlined />}
        />
      ),
    },
    {
      title: "Số điện thoại",
      dataIndex: "phone",
      key: "phone",
      align: "center",
      width: 150,
    },
    {
      title: "Chức vụ",
      dataIndex: "position",
      key: "position",
      align: "center",
      width: 150,
    },
    {
      title: "Hành động",
      key: "action",
      align: "center",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditContact(record)}
          />
          <Popconfirm
            title="Xóa liên hệ"
            description="Bạn có chắc chắn muốn xóa liên hệ này?"
            onConfirm={() => handleDeleteContact(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenModalAdd(true)}
        >
          Thêm liên hệ
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data?.data || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: data?.pagination.total || 0,
          onChange: (page, pageSize) => {
            setParams((prev) => ({
              ...prev,
              page,
              limit: pageSize,
            }));
          },
        }}
        scroll={{ x: 800 }}
      />

      <ModalAddContact
        open={openModalAdd}
        onCancel={() => setOpenModalAdd(false)}
        onSuccess={() => {
          setOpenModalAdd(false);
        }}
      />

      <ModalEditContact
        open={openModalEdit}
        record={selectedContact}
        params={params}
        onCancel={() => {
          setOpenModalEdit(false);
          setSelectedContact(null);
        }}
        onSuccess={() => {
          setOpenModalEdit(false);
          setSelectedContact(null);
        }}
      />
    </div>
  );
};

export default Contacts;
